/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { ConversationParticipant, ConversationUserReference } from '@centecomm/portal-service';
import { useConversations } from '@centecomm/portal-service-react';
import { ArrowLeftCircle, IconColors } from 'Icons';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FlexWrapper } from 'Shared/FlexWrapper';
import { LoadingContent } from 'Shared/PageElements';
import { Search } from 'Shared/Search';
import { conversationsActions, conversationsSelectors } from 'State';
import { ConversationsActionTypes } from 'State/Conversations/actions';
import { addErrorMessage, addSuccessMessage } from 'State/GlobalMessages/actionCreators';
import { hasValidRecord } from 'utils';
import styles from './conversation-users-panel.module.css';

interface ConversationUsersPanelProps {
    conversationId: number;
    onGoBackHandler?: () => void;
    onAddNewUser?: () => void;
}

function ConversationUsersPanel({
    conversationId,
    onGoBackHandler,
    onAddNewUser,
}: ConversationUsersPanelProps): React.ReactElement<ConversationUsersPanelProps> {
    const { t } = useTranslation(['conversationsFeature']);
    const dispatch = useDispatch();

    const { addUserToConversation, getAvailableUsersConversations } = useConversations();

    const availableUsers: ConversationUserReference[] = useSelector(
        conversationsSelectors.availableUsersForConversation(conversationId),
    );
    const availableUsersLoaded: boolean = useSelector(
        conversationsSelectors.availableUsersLoadedForConversation(conversationId),
    );
    const participants: ConversationParticipant[] = useSelector(
        conversationsSelectors.participantsForConversation(conversationId),
    );

    const [filteredUsers, setFilteredUsers] = React.useState<ConversationUserReference[]>([]);

    useEffect(() => {
        if (!availableUsersLoaded) {
            getAvailableUsersConversations(conversationId).then((userReferences: ConversationUserReference[]) => {
                dispatch({
                    type: conversationsActions.SET_AVAILABLE_USERS,
                    payload: {
                        conversationId,
                        availableUsers: userReferences,
                    },
                } as ConversationsActionTypes);
            });
        }

        if (availableUsersLoaded && !!availableUsers.length) {
            const participantIds: number[] = participants.map((p: ConversationParticipant) => p.userId);

            setFilteredUsers(availableUsers.filter((u: ConversationUserReference) => !participantIds.includes(u.userId)));
        }
    }, [availableUsersLoaded, availableUsers.length, participants.length]);

    const searchHandler = (searchTerm: string): void => {
        const term: string = searchTerm.toLowerCase();
        const activeParticipantIds: number[] = participants
            .filter((p: ConversationParticipant) => p.isActive)
            .map((p: ConversationParticipant) => p.userId);

        const filtered: ConversationUserReference[] = availableUsers
            .filter((u: ConversationUserReference) => !activeParticipantIds.includes(u.userId))
            .filter((u: ConversationUserReference) => u.userName.toLowerCase().includes(term));

        setFilteredUsers(filtered);
    };

    const clearSearch = () => {
        const participantIds: number[] = participants.map((p: ConversationParticipant) => p.userId);

        setFilteredUsers(availableUsers.filter((u: ConversationUserReference) => !participantIds.includes(u.userId)));
    };

    const addUser = (user: ConversationUserReference) => {
        const { userId, userName } = user;

        if (
            participants.map((p: ConversationParticipant) => p.userId).includes(userId) &&
            participants.filter((p) => p.userId === userId)[0].isActive
        )
            return;

        addUserToConversation(conversationId, userId)
            .then(() => {
                if (onAddNewUser) onAddNewUser();

                dispatch(
                    addSuccessMessage(t('conversationsFeature:usersPanel.actions.add.success', { username: userName })),
                );
            })
            .catch(() => {
                dispatch(
                    addErrorMessage(
                        t('conversationsFeature:usersPanel.actions.add.errors.withUsername', { username: userName }),
                    ),
                );
            });
    };

    if (!availableUsersLoaded) return <LoadingContent />;

    return (
        <div className={styles.ConversationUsersPanel}>
            <FlexWrapper align="start" justify="start" gap="0.3rem">
                {onGoBackHandler && (
                    <span onClick={onGoBackHandler}>
                        <ArrowLeftCircle color={IconColors.Action} aspect="1.1rem" />
                    </span>
                )}
                <h2>{t('conversationsFeature:usersPanel.actions.add.title')}</h2>
            </FlexWrapper>
            {availableUsers ? (
                <>
                    <Search
                        handler={searchHandler}
                        placeholder={t('conversationsFeature:usersPanel.searchPlaceholder')}
                        clearCallback={clearSearch}
                    />
                    <ul className={styles.UsersList}>
                        {filteredUsers.map((userReference: ConversationUserReference) => (
                            <li
                                key={`users-panel-${userReference.userId}`}
                                className={styles.User}
                                onClick={() => {
                                    addUser(userReference);
                                }}
                            >
                                <p className={styles.UserName}>{userReference.userName}</p>
                                {hasValidRecord(userReference.email) && (
                                    <p className={styles.UserEmail}>{userReference.email}</p>
                                )}
                            </li>
                        ))}
                    </ul>
                </>
            ) : (
                <LoadingContent message={t('conversationsFeature:usersPanel.noUsers')} />
            )}
        </div>
    );
}

export default ConversationUsersPanel;
